import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  Cloud, Plus, CheckCircle2, Clock, ArrowRight, RefreshCw,
  Server, Brain, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const TIER_COLORS = {
  starter: 'border-gray-200 bg-white',
  professional: 'border-[#D4A843] bg-[#D4A843]/5',
  enterprise: 'border-amber-300 bg-amber-50',
};

export default function MigrationDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Dashboard');
  const [tiers, setTiers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ project_name: '', client_name: '', client_email: '', tier: 'professional', notes: '' });
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAssess, setShowAssess] = useState(false);
  const [assessForm, setAssessForm] = useState({ company_name: '', industry: '', current_setup: '', server_count: '', databases: '', cloud_preference: '', compliance: '', budget: '', pain_points: '', timeline: '' });
  const [assessing, setAssessing] = useState(false);
  const [assessment, setAssessment] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [tiersRes, projRes] = await Promise.all([
          api.get('/api/migration/tiers'),
          api.get('/api/migration/projects'),
        ]);
        setTiers(tiersRes.data.tiers || []);
        setProjects(projRes.data.projects || []);
      } catch (e) { /* handled */ } finally { setLoading(false); }
    })();
  }, []);

  const handleCreate = async () => {
    if (!form.project_name || !form.client_name) { toast.error('Project name and client name required'); return; }
    try {
      const { data } = await api.post('/api/migration/projects', form);
      setProjects(prev => [data, ...prev]);
      setForm({ project_name: '', client_name: '', client_email: '', tier: 'professional', notes: '' });
      setShowCreate(false);
      toast.success(`Project created: ${data.total_steps} steps generated`);
    } catch { toast.error('Failed to create project'); }
  };

  const handleCompleteStep = async (projectId, stepOrder) => {
    try {
      const { data } = await api.put(`/api/migration/projects/${projectId}/steps/${stepOrder}`, { status: 'completed' });
      setProjects(prev => prev.map(p => p.id === projectId ? data : p));
      if (selectedProject?.id === projectId) setSelectedProject(data);
      toast.success('Step completed');
    } catch { toast.error('Update failed'); }
  };

  const handleAssess = async () => {
    if (!assessForm.company_name) { toast.error('Company name required'); return; }
    setAssessing(true);
    try {
      const { data } = await api.post('/api/migration/assess', assessForm);
      setAssessment(data);
      toast.success('Assessment complete');
    } catch { toast.error('Assessment failed'); }
    finally { setAssessing(false); }
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="migration-dashboard">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="max-w-6xl space-y-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Cloud Migration</h1>
              <p className="text-sm text-gray-500 mt-1">Manage migration projects and run AI assessments</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => { setShowAssess(!showAssess); setShowCreate(false); }} variant="outline" className="rounded-lg text-xs" data-testid="mig-assess-btn">
                <Brain className="w-3.5 h-3.5 mr-1" /> AI Assessment
              </Button>
              <Button onClick={() => { setShowCreate(!showCreate); setShowAssess(false); }} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="mig-new-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> New Project
              </Button>
            </div>
          </div>

          {/* AI Assessment Form */}
          {showAssess && (
            <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-xl p-6 text-white animate-fade-in" data-testid="mig-assess-form">
              <div className="flex items-center gap-2 mb-4"><Brain className="w-5 h-5 text-[#D4A843]" /><h3 className="text-sm font-bold uppercase tracking-wider">AI Architecture Assessment</h3></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-300">Company Name *</Label><Input value={assessForm.company_name} onChange={e => setAssessForm({...assessForm, company_name: e.target.value})} className="mt-1 rounded-lg bg-white/10 border-white/20 text-white" data-testid="assess-company" /></div>
                <div><Label className="text-xs text-gray-300">Industry</Label><Input value={assessForm.industry} onChange={e => setAssessForm({...assessForm, industry: e.target.value})} className="mt-1 rounded-lg bg-white/10 border-white/20 text-white" data-testid="assess-industry" /></div>
                <div><Label className="text-xs text-gray-300">Current Setup</Label><Input value={assessForm.current_setup} onChange={e => setAssessForm({...assessForm, current_setup: e.target.value})} className="mt-1 rounded-lg bg-white/10 border-white/20 text-white" placeholder="On-prem, hybrid, etc." data-testid="assess-setup" /></div>
                <div><Label className="text-xs text-gray-300">Server Count</Label><Input value={assessForm.server_count} onChange={e => setAssessForm({...assessForm, server_count: e.target.value})} className="mt-1 rounded-lg bg-white/10 border-white/20 text-white" data-testid="assess-servers" /></div>
                <div><Label className="text-xs text-gray-300">Cloud Preference</Label><Input value={assessForm.cloud_preference} onChange={e => setAssessForm({...assessForm, cloud_preference: e.target.value})} className="mt-1 rounded-lg bg-white/10 border-white/20 text-white" placeholder="AWS, Azure, GCP..." data-testid="assess-cloud" /></div>
                <div><Label className="text-xs text-gray-300">Budget Range</Label><Input value={assessForm.budget} onChange={e => setAssessForm({...assessForm, budget: e.target.value})} className="mt-1 rounded-lg bg-white/10 border-white/20 text-white" placeholder="$5,000 - $15,000" data-testid="assess-budget" /></div>
              </div>
              <div className="mt-3"><Label className="text-xs text-gray-300">Pain Points</Label><Textarea value={assessForm.pain_points} onChange={e => setAssessForm({...assessForm, pain_points: e.target.value})} className="mt-1 rounded-lg bg-white/10 border-white/20 text-white" rows={2} placeholder="Slow systems, downtime, scaling issues..." data-testid="assess-pain" /></div>
              <Button onClick={handleAssess} disabled={assessing} className="mt-3 bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="assess-submit-btn">
                {assessing ? 'Analyzing...' : 'Run AI Assessment'}
              </Button>
            </div>
          )}

          {/* Assessment Results */}
          {assessment && !assessment.error && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in" data-testid="assess-results">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Assessment Results</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-[10px] text-gray-400 uppercase">Risk Level</p><p className={`text-sm font-bold ${assessment.risk_level === 'high' ? 'text-red-600' : assessment.risk_level === 'medium' ? 'text-amber-600' : 'text-emerald-600'}`}>{assessment.risk_level}</p></div>
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-[10px] text-gray-400 uppercase">Complexity</p><p className="text-sm font-bold text-gray-900">{assessment.complexity}</p></div>
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-[10px] text-gray-400 uppercase">Recommended Tier</p><p className="text-sm font-bold text-[#D4A843] capitalize">{assessment.recommended_tier}</p></div>
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-[10px] text-gray-400 uppercase">Timeline</p><p className="text-sm font-bold text-gray-900">{assessment.estimated_timeline}</p></div>
              </div>
              <p className="text-sm text-gray-600 mb-3">{assessment.summary}</p>
              {assessment.key_risks?.length > 0 && (
                <div className="mb-3"><p className="text-[10px] text-gray-400 uppercase mb-1">Key Risks</p>
                  <ul className="space-y-1">{assessment.key_risks.map((r, i) => <li key={`item-${i}`} className="text-xs text-gray-600 flex items-start gap-1.5"><ChevronRight className="w-3 h-3 text-red-500 shrink-0 mt-0.5" />{r}</li>)}</ul>
                </div>
              )}
              <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => setAssessment(null)}>Dismiss</Button>
            </div>
          )}

          {/* Create Project Form */}
          {showCreate && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4 animate-fade-in" data-testid="mig-create-form">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">New Migration Project</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-xs">Project Name *</Label><Input value={form.project_name} onChange={e => setForm({...form, project_name: e.target.value})} className="mt-1 rounded-lg" placeholder="Acme Corp Cloud Migration" data-testid="mig-name" /></div>
                <div><Label className="text-xs">Client Name *</Label><Input value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} className="mt-1 rounded-lg" data-testid="mig-client" /></div>
                <div><Label className="text-xs">Client Email</Label><Input value={form.client_email} onChange={e => setForm({...form, client_email: e.target.value})} className="mt-1 rounded-lg" data-testid="mig-email" /></div>
                <div><Label className="text-xs">Service Tier</Label>
                  <Select value={form.tier} onValueChange={v => setForm({...form, tier: v})}>
                    <SelectTrigger className="mt-1 rounded-lg" data-testid="mig-tier-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {tiers.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.price_range})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1 rounded-lg" rows={2} data-testid="mig-notes" /></div>
              <Button onClick={handleCreate} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="mig-create-btn">Create Project</Button>
            </div>
          )}

          {/* Project Detail */}
          {selectedProject && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in" data-testid="mig-detail">
              <button onClick={() => setSelectedProject(null)} className="text-xs text-[#D4A843] hover:underline mb-3 block" data-testid="mig-back">Back to projects</button>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{selectedProject.project_name}</h3>
                  <p className="text-xs text-gray-500">{selectedProject.client_name} | {selectedProject.tier_name} | {selectedProject.completed_steps}/{selectedProject.total_steps} steps</p>
                </div>
                <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${selectedProject.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-[#D4A843]/10 text-[#D4A843]'}`}>{selectedProject.status}</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full mb-6">
                <div className="h-full bg-[#D4A843] rounded-full transition-all" style={{ width: `${(selectedProject.completed_steps / selectedProject.total_steps) * 100}%` }} />
              </div>
              <div className="space-y-3">
                {(selectedProject.steps || []).map(step => (
                  <div key={step.order} className={`flex items-center gap-4 p-4 rounded-lg ${step.status === 'completed' ? 'bg-emerald-50' : 'bg-gray-50'}`} data-testid={`mig-step-${step.order}`}>
                    <button onClick={() => step.status !== 'completed' && handleCompleteStep(selectedProject.id, step.order)} disabled={step.status === 'completed'}
                      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${step.status === 'completed' ? 'bg-emerald-500 text-white' : 'border-2 border-gray-300 hover:border-[#D4A843]'}`}
                      data-testid={`mig-complete-${step.order}`}>
                      {step.status === 'completed' ? <CheckCircle2 className="w-5 h-5" /> : <span className="text-xs text-gray-400">{step.order}</span>}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${step.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{step.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">Deliverable: {step.deliverable}</p>
                    </div>
                    <span className="text-[10px] text-gray-400 shrink-0">{step.sla_hours}h SLA</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Project List */}
          {!selectedProject && (
            <div className="space-y-3" data-testid="mig-project-list">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">{projects.length} Projects</h3>
              {projects.map(p => (
                <button key={p.id} onClick={() => setSelectedProject(p)} className={`w-full text-left border-2 rounded-lg p-5 hover:shadow-md transition-all ${TIER_COLORS[p.tier] || 'border-gray-200 bg-white'}`} data-testid={`mig-project-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Cloud className="w-4 h-4 text-[#D4A843]" />
                        <h4 className="text-sm font-bold text-gray-900">{p.project_name}</h4>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${p.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-[#D4A843]/10 text-[#D4A843]'}`}>{p.status}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{p.client_name} | {p.tier_name} | Created {new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-lg font-bold text-[#D4A843]">{p.completed_steps}/{p.total_steps}</p>
                        <p className="text-[10px] text-gray-400">steps</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full mt-3">
                    <div className="h-full bg-[#D4A843] rounded-full transition-all" style={{ width: `${(p.completed_steps / p.total_steps) * 100}%` }} />
                  </div>
                </button>
              ))}
              {projects.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No migration projects yet. Create one or run an AI assessment.</p>}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
